import React from 'react';
// import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Divider,
} from '@mui/material';

import creyLogo from '../assets/creyos/creyos-logo.png';
import tokenSearch from '../assets/creyos/token-search.png';
import doubleTrouble from '../assets/creyos/double-trouble.png';
import rotations from '../assets/creyos/rotations.png';
import digitSpan from '../assets/creyos/digit-span.png';
import spatialPlanning from '../assets/creyos/spatial-planning.png';
import polygons from '../assets/creyos/polygons.png';
import creyosBackground from '../assets/creyos/creyos-background.webp';
const zocdocLink =
  'https://www.zocdoc.com/practice/aspire-bhp-179924?lock=true&isNewPatient=false&referrerType=widget';
  
const highlights = [
  'Attention and focus',
  'Working and long-term memory',
  'Processing speed',
  'Reasoning and problem solving',
  'Cognitive flexibility',
  'Verbal skills',
];

const tasks = [
  { title: 'Token Search', image: tokenSearch },
  { title: 'Double Trouble', image: doubleTrouble },
  { title: 'Rotations', image: rotations },
  { title: 'Digit Span', image: digitSpan },
  { title: 'Spatial Planning', image: spatialPlanning },
  { title: 'Polygons', image: polygons },
];

const uses = [
  {
    title: 'Who Is Creyos For?',
    description:
      'Creyos may be helpful for adults experiencing memory concerns, focus difficulties, brain fog, depression, anxiety, or cognitive changes. It can also be used for baseline cognitive health screening.',
  },
  {
    title: 'Clinical Uses',
    description:
      'Creyos can support cognitive screening, treatment planning, progress monitoring, and objective measurement of cognitive changes over time.',
  },
];

export default function Creyos() {
  return (
    <>
      <Helmet>
        <title>Creyos Cognitive Assessment | Aspire Behavioral Health</title>
        <meta
          name="description"
          content="Creyos cognitive assessments help measure memory, attention, processing speed, reasoning, and overall brain performance."
        />
      </Helmet>

      {/* Fixed Background for Parallax Effect */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100vh',
          backgroundImage: `url(${creyosBackground})`,
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: {
            xs: 'center top',
            sm: 'center center',
            md: 'center center',
          },
          zIndex: -2,
        }}
      />

      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          minHeight: {
            xs: 520,
            sm: 580,
            md: 650,
            lg: 620,
          },
          py: {
            xs: 8,
            sm: 10,
            md: 12,
            lg: 10,
          },
          overflow: 'hidden',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          color: 'white',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.45)',
            zIndex: 1,
          }}
        />

        <Box
          sx={{
            position: 'relative',
            zIndex: 2,
            px: 3,
            maxWidth: '850px',
          }}
        >
          <Typography
            variant="h2"
            sx={{
              fontWeight: 600,
              mb: 2,
              fontSize: {
                xs: '2rem',
                sm: '2.5rem',
                md: '3rem',
                lg: '3.5rem',
              },
              lineHeight: 1.2,
            }}
          >
            Creyos Cognitive Assessment
          </Typography>

          <Typography
            variant="h6"
            sx={{
              fontWeight: 300,
              lineHeight: 1.8,
              mb: 4,
              fontSize: {
                xs: '1rem',
                sm: '1.1rem',
                md: '1.2rem',
              },
            }}
          >
            A science-based cognitive assessment designed to better understand
            memory, attention, reasoning, and overall brain performance.
          </Typography>

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant="contained"
              href="tel:2108669850"
              sx={{
                backgroundColor: '#e3f3c5',
                color: 'black',
                fontWeight: 600,
                px: 4,
                py: 1.3,
                borderRadius: '30px',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#d3eab0',
                },
              }}
            >
              Call 210-866-9850
            </Button>

            <Button
                          component="a"
                          href={zocdocLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="outlined"
                          sx={{
                            borderColor: 'white',
                            color: 'white !important',
                            fontWeight: 600,
                            px: 4,
                            py: 1.3,
                            borderRadius: '30px',
                            textTransform: 'none',
                            '&:visited': {
                              color: 'white',
                            },
                            '&:hover': {
                              borderColor: 'white',
                              backgroundColor: 'rgba(255,255,255,0.1)',
                              color: 'white',
                            },
                          }}
                        >
                          Schedule an Appointment
                        </Button>
          </Box>
        </Box>
      </Box>

      {/* Main Content */}
      <Box
        sx={{
          backgroundColor: 'white',
          py: 8,
          px: 2,
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* Intro */}
        <Box sx={{ textAlign: 'center', maxWidth: '900px', mx: 'auto', mb: 7 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
            Understanding Cognitive Health
          </Typography>

          <Typography
            variant="body1"
            sx={{
              lineHeight: 1.9,
              color: 'text.secondary',
              fontSize: { xs: '1rem', sm: '1.05rem' },
            }}
          >
            Your cognitive health is just as important as your physical health.
            Creyos assessments help measure important brain functions including
            memory, attention, processing speed, and reasoning.
            <br />
            <br />
            At Aspire Behavioral Health, we use Creyos assessments to gather
            objective information that can support treatment planning, monitor
            progress, and identify subtle cognitive changes over time.
            <br />
            <br />
            Whether you are experiencing brain fog, focus difficulties, memory
            concerns, or simply want a baseline understanding of your cognitive
            health, Creyos provides a science-based approach to evaluation.
            <br />
            <br />
            To schedule your assessment, call{' '}
            <a href="tel:2108669850">210-866-9850</a>.
          </Typography>
        </Box>

        <Divider
          sx={{
            width: { xs: '80%', sm: '60%', md: '40%' },
            mx: 'auto',
            mb: 7,
            borderColor: '#d9d9d9',
          }}
        />

        {/* What Creyos Measures */}
        <Box
          sx={{
            backgroundColor: '#e3f3c5',
            color: 'black',
            maxWidth: '1200px',
            mx: 'auto',
            p: { xs: 3, sm: 5 },
            borderRadius: 4,
            boxShadow: '0px 4px 12px rgba(0,0,0,0.12)',
            mb: 8,
          }}
        >
          <Box
            component="img"
            src={creyLogo}
            alt="Creyos Logo"
            sx={{
              display: 'block',
              mx: 'auto',
              width: '100%',
              maxWidth: 220,
              mb: 3,
            }}
          />

          <Typography
            variant="h4"
            sx={{ textAlign: 'center', fontWeight: 600, mb: 2 }}
          >
            What Creyos Measures
          </Typography>

          <Typography
            variant="body1"
            sx={{
              textAlign: 'center',
              maxWidth: '750px',
              mx: 'auto',
              mb: 4,
              lineHeight: 1.7,
            }}
          >
            Creyos evaluates multiple cognitive domains to provide a clearer
            picture of overall brain performance.
          </Typography>

          <Grid container spacing={2} justifyContent="center">
            {highlights.map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item}>
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    boxShadow: 'none',
                    backgroundColor: 'rgba(255,255,255,0.75)',
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', p: 2.5 }}>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {item}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Cognitive Tasks */}
        <Box sx={{ maxWidth: '1200px', mx: 'auto', mb: 8 }}>
          <Typography
            variant="h4"
            sx={{
              textAlign: 'center',
              fontWeight: 600,
              mb: 2,
            }}
          >
            Example Cognitive Tasks
          </Typography>

          <Typography
            variant="body1"
            sx={{
              textAlign: 'center',
              maxWidth: '750px',
              mx: 'auto',
              mb: 5,
              lineHeight: 1.8,
              color: 'text.secondary',
            }}
          >
            Creyos uses interactive cognitive tasks designed to evaluate
            different areas of brain function in a structured and objective way.
          </Typography>

          <Grid container spacing={3} justifyContent="center">
            {tasks.map((task) => (
              <Grid item xs={6} sm={4} md={2} key={task.title}>
                <Card
                  sx={{
                    borderRadius: 4,
                    textAlign: 'center',
                    boxShadow: '0px 4px 14px rgba(0,0,0,0.1)',
                    height: '100%',
                  }}
                >
                  <CardContent>
                    <Box
                      component="img"
                      src={task.image}
                      alt={task.title}
                      sx={{
                        width: '100%',
                        maxWidth: 120,
                        borderRadius: 2,
                        mb: 2,
                      }}
                    />

                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                      }}
                    >
                      {task.title}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Uses */}
        <Box sx={{ maxWidth: '1000px', mx: 'auto' }}>
          <Typography
            variant="h4"
            sx={{
              textAlign: 'center',
              fontWeight: 600,
              mb: 5,
            }}
          >
            How Creyos Can Help
          </Typography>

          <Grid container spacing={4}>
            {uses.map((item) => (
              <Grid item xs={12} md={6} key={item.title}>
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 4,
                    boxShadow: '0px 4px 14px rgba(0,0,0,0.1)',
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 600,
                        mb: 2,
                      }}
                    >
                      {item.title}
                    </Typography>

                    <Typography
                      sx={{
                        lineHeight: 1.9,
                        color: 'text.secondary',
                      }}
                    >
                      {item.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Bottom CTA */}
          <Box sx={{ textAlign: 'center', mt: 7 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
              Ready to schedule your cognitive assessment?
            </Typography>

            <Box
              sx={{
                display: 'flex',
                gap: 2,
                justifyContent: 'center',
                flexWrap: 'wrap',
              }}
            >
              <Button
                variant="contained"
                href="tel:2108669850"
                sx={{
                  backgroundColor: '#e3f3c5',
                  color: 'black',
                  fontWeight: 600,
                  px: 4,
                  py: 1.3,
                  borderRadius: '30px',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#d3eab0',
                  },
                }}
              >
                Call 210-866-9850
              </Button>

              <Button
                              component="a"
                              href={zocdocLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              variant="outlined"
                              sx={{
                                backgroundColor: 'transparent',
                                border: '2px solid #3a4c17',
                                color: '#3a4c17 !important',
                                fontWeight: 600,
                                px: 4,
                                py: 1.3,
                                borderRadius: '30px',
                                textTransform: 'none',
                                '&:visited': {
                                  color: '#3a4c17',
                                },
                                '&:hover': {
                                  backgroundColor: 'rgba(58,76,23,0.05)',
                                  border: '2px solid #3a4c17',
                                  color: '#3a4c17',
                                },
                              }}
                            >
                              Schedule an Appointment
                            </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
}